import * as moment from 'moment';
import * as _ from 'lodash';
import { useState, useMemo } from 'react';
import { IUser } from '@nkr/models/index';
import { UserGroup } from '@nkr/enums/index';
import { BodyScrollEvent, FirstDataRenderedEvent, GridColumnsChangedEvent, GridReadyEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Glyphicon, Label } from 'react-bootstrap';
import { ActionButtonRenderer, UserAction } from './action-button-renderer';
import { GridWrapper } from './styles';
import SortableHeaderComponent from '../admin/center/list/common/sortable-header';

interface IActionRendererProps extends ICellRendererParams { onItemSelected: (id: number, action: UserAction) => void, onMenuToggle: (show: boolean) => void };

interface IProps {
    list: IUser[],
    isAdmin: boolean,
    currentUserGroupId: UserGroup,
    filterGroupId: number,
    onItemSelected: (id: number, action: UserAction) => void
}

const UserListGrid = (props: IProps) => {
    const [menuDisplayed, setMenuDisplayed] = useState(false);

    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onBodyScroll = (e: BodyScrollEvent) => {
        if (menuDisplayed) {
            document.body.click();
            setMenuDisplayed(false);
        }
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onGridColumnsChanged = (e: GridColumnsChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const handleMenuToggle = (show: boolean) => {
        setMenuDisplayed(show);
    };

    const cols = [
        {
            colId: 'firstName',
            field: 'firstName',
            headerName: 'First Name',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>First Name</SortableHeaderComponent>,
            width: 85,
        },
        {
            colId: 'lastName',
            field: 'lastName',
            headerName: 'Last Name',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Last Name</SortableHeaderComponent>,
            width: 85,
        },
        {
            colId: 'email',
            field: 'email',
            headerName: 'Email',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Email</SortableHeaderComponent>,
            width: 175,
        },
        {
            colId: 'username',
            field: 'username',
            headerName: 'User Name',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>User Name</SortableHeaderComponent>,
            width: 85,
        },
        {
            colId: 'groupDescription',
            field: 'group.name',
            headerName: 'Group',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Group</SortableHeaderComponent>,
            width: 95,
            hide: props.isAdmin == false
        },
        {
            colId: 'center',
            field: 'centerAlias',
            headerName: 'Center',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
            width: 95,
            hide: props.isAdmin == false || (props.filterGroupId != UserGroup.CenterAdmin && props.filterGroupId != UserGroup.CenterUser && props.filterGroupId != 0)
        },
        {
            colId: 'courierVendor',
            field: 'courierVendorName',
            headerName: 'Courier Vendor',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Courier Vendor</SortableHeaderComponent>,
            width: 95,
            hide: props.isAdmin == false || (props.filterGroupId != UserGroup.LogisticsProvider && props.filterGroupId != UserGroup.LogisticsPhlebotUser && props.filterGroupId != 0)
        },
        {
            colId: 'opo',
            field: 'opoName',
            headerName: 'OPO',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>OPO</SortableHeaderComponent>,
            width: 95,
            hide: props.isAdmin == false || (props.filterGroupId != UserGroup.Opo && props.filterGroupId != UserGroup.OpoAdmin && props.filterGroupId != 0)
        },
        {
            colId: 'organType',
            field: 'organType',
            headerName: 'Organ Type',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Organ Type</SortableHeaderComponent>,
            width: 65,
            hide: props.isAdmin == false,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IUser;

                if (data.organTypeId == null) {
                    return <>- EITHER -</>
                }

                return <>{data.organType.toUpperCase()}</>
            }
        },
        {
            colId: 'lastVisit',
            field: 'lastVisit',
            headerName: 'Last Visit',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Last Visit</SortableHeaderComponent>,
            width: 125,
            cellRenderer: (params: ICellRendererParams) => params.data.lastVisit,
            valueGetter: (params: ValueGetterParams) => params.data.lastVisit != '' ? parseInt(moment(params.data.lastVisit, 'MM/DD/YYYY').format('YYYYMMDD')) : -1
        },
        {
            colId: 'lastLoginAttemptIp',
            field: 'lastLoginAttemptIp',
            headerName: 'Last Visit',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>IP BFM</SortableHeaderComponent>,
            width: 100,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IUser;

                if (data.lastLoginAttemptIp != '') {
                    return <>{data.lastLoginAttemptIp} <Label bsStyle={data.lastLoginAttemptCount >= 3 ? 'danger' : 'default'}>{data.lastLoginAttemptCount}</Label></>;
                }

                return null;
            }
        },
        {
            colId: 'lastLoginAttemptCount',
            field: 'lastLoginAttemptCount',
            headerName: 'User BFM',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>User<br />BFM</SortableHeaderComponent>,
            width: 45,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IUser;
                return <Label bsStyle={data.lastLoginAttemptCount >= 3 ? 'danger' : 'default'}>{data.lastLoginAttemptCount}</Label>;
            }
        },
        {
            colId: 'isLockedOut',
            field: 'isLockedOut',
            headerName: 'Can Login',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Can<br />Login</SortableHeaderComponent>,
            width: 45,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IUser;
                return data.isLockedOut == false ? <Glyphicon glyph='ok' /> : null;
            }
        },
        {
            colId: 'isDeactivated',
            field: 'isDeactivated',
            headerName: 'Is Deactivated',
            width: 65,
            hide: props.isAdmin == false,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Is<br />Deactivated</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IUser;
                return <>{data.isDeactivated ? 'Yes' : 'No'}</>
            },
            valueGetter: (params: ValueGetterParams) => params.data.isDeactivated ? 'Yes' : 'No'
        },
        {
            colId: 'isTwoFactorEnabled',
            field: 'isTwoFactorEnabled',
            headerName: 'Two Factor Login Enabled',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Two-Factor<br />Enabled</SortableHeaderComponent>,
            width: 65,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IUser;
                return data.isTwoFactorEnabled ? <Glyphicon glyph='ok' /> : null;
            }
        },
        {
            colId: 'expirePasswordDateTime',
            field: 'expirePasswordDateTime',
            headerName: 'Last Visit',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Password<br />Expiration</SortableHeaderComponent>,
            width: 65,
        },
        {
            colId: 'action',
            field: 'id',
            headerName: '',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}></SortableHeaderComponent>,
            width: 65,
            cellRendererParams: { onMenuToggle: handleMenuToggle, onItemSelected: props.onItemSelected },
            cellRendererFramework: (params: IActionRendererProps) => (
                <ActionButtonRenderer
                    {...params}
                    pullRight={true}
                    isAdmin={props.isAdmin}
                    isAdminLogin={false}
                    userGroupId={params.data.groupId}
                    currentUserGroupId={props.currentUserGroupId}
                    data={params.data}
                    onMenuToggle={params.onMenuToggle}
                    onItemSelected={params.onItemSelected}
                />
            )
        }
    ];

    const columnDefs = useMemo(() => cols, [props.filterGroupId]);
    const rowData = useMemo(() => props.list, [props.list]);

    console.log(props.filterGroupId)

    return (
        <GridWrapper className='ag-theme-bootstrap' style={{ height: props.isAdmin ? '73vh' : '65vh' }}>
            <AgGridReact
                defaultColDef={{
                    headerClass: 'list-header',
                    cellClass: 'list-cell',
                    sortable: true,
                    resizable: false
                }}
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={40}
                rowHeight={30}
                suppressRowClickSelection={false}
                suppressCellSelection={true}
                // immutableData={true}
                noRowsOverlayComponentFramework={() => <div>No users found...</div>}
                suppressContextMenu={true}
                onGridReady={onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onRowDataChanged={onRowDataChanged}
                onBodyScroll={onBodyScroll}
                onGridColumnsChanged={onGridColumnsChanged}
                getRowNodeId={(data: IUser) => data.id.toString()}
            />
        </GridWrapper>
    );
}

export default UserListGrid;